/**
 * `SCR-REVIEW` (25 states) and `SCR-PLACED` (4). FLOWS_SPEC §7.
 * OWNER: web-frontend-engineer.
 *
 * FULL DISCLOSURE: the complete written specification, every option value in
 * plain language, the cloth, the bound record, the metreage, the price and the
 * acknowledgements. NOTHING COLLAPSED, nothing behind a disclosure triangle,
 * nothing "see details". Scrolling is acceptable; hiding is not.
 *
 * The colour block is `TwoPathChoice` — RULING-008. Two paths, NEITHER
 * PRE-SELECTED, swatch-first listed first, no alert affordance and no way to
 * acquire one. The acknowledgement is BOUND TO THE CLOTH: any change to
 * `fabric.primary` voids it and the block is presented fresh
 * (`ST-REVIEW-COLOUR-VOIDED`), with the previous choice NOT carried forward —
 * carrying it would be consent laundering across a different object.
 */

import { useEffect, useRef, useState } from 'react';
import { t, tDynamic } from '@pt/copy';
import type { SubmittedAcknowledgement } from '@pt/core';
import type { ReviewPayload } from '../lib/contracts.ts';
import { ApiRejection, api } from '../lib/api.ts';
import { refusalCopy } from '../lib/refusal.ts';
import { useResource, useSubmission } from '../lib/resource.ts';
import { forgetDraft, heldDraftId } from '../lib/draft.ts';
import { commissionTypeLabel } from '../lib/optionCopy.ts';
import { compositionLine, dateInWords, decimal, joinList, metres, money } from '@pt/view';
import { patternLabel, present, weaveLabel } from '../lib/label.ts';
import { AppShell } from '../app/AppShell.tsx';
import { useNavigation } from '../app/Router.tsx';
import { useAnnouncer } from '../app/Announcer.tsx';
import { useHouse } from '../app/SessionProvider.tsx';
import { PriceDisplay, TwoPathChoice } from '../components/commission.tsx';
import { AvailabilityLine, ColourStatement } from '../components/cloth.tsx';
import {
  Button,
  ErrorStatement,
  FactLine,
  SelectionMark,
  Statement,
  TextInput,
} from '../components/primitives.tsx';
import { FetchError, LoadingRegion, OfflineStatement } from '../components/states.tsx';

type ColourPath = 'swatch_first' | 'proceed_now';

export function ReviewScreen() {
  const { navigate } = useNavigation();
  const { tailor } = useHouse();
  const { say, refuse } = useAnnouncer();
  const draftId = heldDraftId();
  const review = useResource(() => api.review(draftId ?? ''), [draftId]);
  const submission = useSubmission();

  const [colourPath, setColourPath] = useState<ColourPath | null>(null);
  const [accepted, setAccepted] = useState<Readonly<Record<string, boolean>>>({});
  const [address, setAddress] = useState('');
  const [contact, setContact] = useState('');
  const [fitting, setFitting] = useState('');
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [swatchFirstRecorded, setSwatchFirstRecorded] = useState(false);
  const refusalRef = useRef<HTMLDivElement>(null);
  const boundFabric = useRef<string | null>(null);

  const data = review.data;
  /* NULL UNTIL A CLOTH IS CHOSEN, and that is a real state rather than a
     defensive `?`: a draft can carry a full specification with no cloth on it,
     and the seeded client had exactly one. This used to be `{} as ClothSummary`
     in the adapter, so `compositionLine(undefined)` threw and this screen — the
     one a man reads before spending four thousand dollars — was a blank error
     page. */
  const cloth = data?.cloth ?? null;

  useEffect(() => {
    if (data === null) return;
    setAddress(data.details.deliveryAddress);
    setContact(data.details.contact);
    setFitting(data.details.fittingPreference);
  }, [data]);

  // ST-REVIEW-COLOUR-VOIDED: announced ONCE, politely, on arrival. Focus is not
  // stolen. It never fires twice for the same cloth, so it is not nagging.
  useEffect(() => {
    if (data === null) return;
    if (cloth === null) return;
    if (data.colourAck.voided && boundFabric.current !== cloth.id) {
      boundFabric.current = cloth.id;
      setColourPath(null);
      say(t('review.colourVoided.announce', { clothName: cloth.name }));
    }
  }, [data, say]);

  useEffect(() => {
    if (data?.revalidated?.changed === false) say(t('review.revalidated.unchanged'));
  }, [data, say]);

  const rejection = submission.rejection;
  useEffect(() => {
    if (rejection === null) return;
    refuse(refusalCopy(rejection));
    refusalRef.current?.focus();
  }, [rejection, refuse]);

  const pendingAcks =
    data === null
      ? []
      : data.acknowledgements.filter((ack) => ack.acceptedAt === null && ack.blocksOrder);
  const allAcksTaken = pendingAcks.every((ack) => accepted[ack.acknowledgementId] === true);

  function place() {
    if (data === null || draftId === null) return;
    setPressed(true);
    if (colourPath === null || !allAcksTaken || cloth === null) return;

    if (colourPath === 'swatch_first') {
      // ST-REVIEW-COLOUR-SWATCHFIRST — costs nothing: the complete
      // configuration is preserved as a draft and NO ORDER IS PLACED.
      void submission
        .run(() => api.requestSwatch({ clothId: cloth.id }))
        .then((result) => {
          if (result !== null) setSwatchFirstRecorded(true);
        });
      return;
    }

    // `SubmittedAcknowledgement` carries the BINDING, not the text: the
    // sentence is server-authored, so a forged one cannot be stored (D-2).
    // What the client states is which cloth he was looking at and on what
    // colour basis, and P-FAB-05 checks both against the cloth being cut.
    const acknowledgements: SubmittedAcknowledgement[] = [
      ...data.acknowledgements
        .filter((ack) => accepted[ack.acknowledgementId] === true)
        .map((ack) => ({
          acknowledgementId: ack.acknowledgementId,
          version: ack.version,
          acceptedAt: new Date().toISOString(),
          fabricId: null,
          deltaEShown: null,
          deltaEBasis: null,
        })),
      {
        acknowledgementId: data.colourAck.acknowledgementId,
        version: data.colourAck.version,
        acceptedAt: new Date().toISOString(),
        // [T-46b] — bound to the cloth actually being cut.
        fabricId: cloth.id,
        // null in Round 1: no ΔE measured, so none claimed ([T-39]).
        deltaEShown: data.colourAck.deltaEShown,
        // rules 1.3.2: the cloth's BASIS as he read it. P-FAB-05 voids the
        // acknowledgement if the basis moves under it — the day a cloth is
        // measured, what he read stops being what we can say.
        deltaEBasis: data.colourAck.deltaEBasis,
        pathChosen: 'proceed_now',
      },
    ];

    void submission
      .run(() =>
        // NO PRICE FIELD IS EVER SENT (P-ORD-02).
        api.placeOrder({
          draftId,
          quoteId: data.quote.quoteId,
          configHash: data.quote.configHash,
          acknowledgements,
          deliveryAddress: address,
          contact,
          fittingPreference: fitting,
        }),
      )
      .then((placed) => {
        if (placed === null) return;
        forgetDraft();
        navigate(`/placed/${placed.orderRef}`);
      });
  }

  return (
    <AppShell
      title={t('review.heading')}
      width="ledger"
      ground="book"
      banner={review.phase === 'offline' ? <OfflineStatement /> : null}
    >
      {review.phase === 'skeleton' || review.phase === 'idle' ? (
        <LoadingRegion rows={10} variant="ledger" slow={review.slow} label={t('review.heading')} />
      ) : null}
      {review.phase === 'error' ? <FetchError whatKey="review.error.what" onRetry={review.reload} /> : null}

      {swatchFirstRecorded && data !== null && cloth !== null ? (
        <Statement>
          {t('review.colourSwatchfirst.recorded', { clothName: cloth.name })}
        </Statement>
      ) : null}

      {/* ST-REVIEW-NOCLOTH — everything he has chosen is held and said so; the
          one action goes back to the step that is missing. Not an error: he has
          not done anything wrong, he has just not finished. */}
      {data !== null && cloth === null ? (
        <section className="pt-section">
          <Statement>{t('review.nocloth.statement')}</Statement>
          <div className="pt-actions">
            <Button onClick={() => navigate('/commission/cloth')}>
              {t('clothstep.none.forward')}
            </Button>
          </div>
        </section>
      ) : null}

      {data === null || cloth === null || swatchFirstRecorded ? null : (
        <>
          {/* (1) The complete written specification. */}
          <section className="pt-section" aria-label={t('review.spec.heading')}>
            <h2 className="pt-cutsheet__blockTitle">{t('review.spec.heading')}</h2>
            <dl>
              {data.options
                .filter((option) => option.currentValue != null)
                .map((option) => (
                  <div className="pt-fact pt-fact--pair" key={option.optionId}>
                    <dt className="pt-fact__label">
                      {tDynamic(`config.option.${option.optionId}.label`)}
                    </dt>
                    <dd className="pt-fact__value">
                      {option.type === 'number' || option.type === 'text'
                        ? String(option.currentValue)
                        : tDynamic(
                            `config.option.${option.optionId}.value.${String(option.currentValue)}`,
                          )}
                    </dd>
                  </div>
                ))}
            </dl>
          </section>

          {/* (2) The measurement binding — the numbers go to the cutter and no
              further (M3). No measurement value appears on this page. */}
          <FactLine>
            {t('review.measBinding', {
              authorName: data.record.authorName,
              takenAtDate: dateInWords(data.record.takenAt),
            })}
          </FactLine>

          {/* ST-REVIEW-RECORDSUPERSEDED — stated, not asked about: there is no
              alternative to approve. */}
          {data.recordSuperseded == null ? null : (
            <Statement>
              {t('review.recordsuperseded.statement', {
                authorName: data.recordSuperseded.authorName,
                takenAtDate: dateInWords(data.recordSuperseded.takenAt),
              })}
            </Statement>
          )}

          {/* (3) The cloth, with EL-COLOUR and EL-AVAILABILITY. */}
          <section className="pt-section" aria-label={t('review.cloth.heading')}>
            <h2 className="pt-cutsheet__blockTitle">{t('review.cloth.heading')}</h2>
            <FactLine>
              {t('cloth.facts', {
                composition: compositionLine(cloth.composition),
                weightGsm: cloth.weight_gsm,
                weaveLabel: weaveLabel(cloth.weave) ?? '',
                widthCm: cloth.width_cm,
              })}
            </FactLine>
            {/* C1, 5 September 2026: a mill line only for a NAMED mill (see
                ClothSummary.mill). */}
            {cloth.mill === null ? null : (
              <FactLine>{t('cloth.mill', { millName: cloth.mill })}</FactLine>
            )}
            <ColourStatement
              clothName={cloth.name}
              pattern={cloth.pattern}
              patternRepeatCm={cloth.patternRepeatCm}
              swatchHref={`/book/${cloth.id}/swatch`}
            />
            <AvailabilityLine
              availableMetres={cloth.availableMetres}
              requirementMetres={cloth.clothRequirementMetres}
              commissionTypeLabel={commissionTypeLabel(
                String(data.configuration['commission.type'] ?? ''),
              )}
            />
          </section>

          {/* (4) THE COLOUR BLOCK. */}
          {data.colourAck.voided ? (
            <Statement announce>
              {t('review.colourVoided.statement', { clothName: cloth.name })}
            </Statement>
          ) : null}
          <TwoPathChoice
            headingKey="review.colour.heading"
            // RULING-008's block, with the ACKNOWLEDGEMENT'S OWN SENTENCE as
            // its statement: the text is the domain owner's, it is what is
            // stored, and [T-46] makes "what he was shown" and "what was
            // recorded" the same string by construction.
            statement={data.colourAck.text}
            options={[
              { id: 'swatch_first', label: t('review.colour.path.swatch') },
              { id: 'proceed_now', label: t('review.colour.path.goahead') },
            ]}
            chosen={colourPath}
            onChoose={(id) => setColourPath(id as ColourPath)}
          />
          {/* It says so AS A STATEMENT, not by disabling itself. */}
          {colourPath === null ? (
            <Statement tone="field">{t('review.colourUnchosen.statement')}</Statement>
          ) : null}

          {/* (5) The other acknowledgements, each separate, each unticked. */}
          <section className="pt-section" aria-label={t('review.acks.heading')}>
            <h2 className="pt-cutsheet__blockTitle">{t('review.acks.heading')}</h2>
            {data.acknowledgements.map((ack) =>
              ack.acceptedAt !== null ? (
                // ST-REVIEW-ACK-ALREADY — record-scoped, never asked twice.
                <FactLine key={ack.acknowledgementId}>
                  {t('review.ackAlready.statement', {
                    acceptedDate: dateInWords(ack.acceptedAt),
                  })}
                </FactLine>
              ) : (
                <button
                  key={ack.acknowledgementId}
                  type="button"
                  role="checkbox"
                  className="pt-twopath__option"
                  aria-checked={accepted[ack.acknowledgementId] === true}
                  onClick={() =>
                    setAccepted((current) => ({
                      ...current,
                      [ack.acknowledgementId]: current[ack.acknowledgementId] !== true,
                    }))
                  }
                >
                  <SelectionMark checked={accepted[ack.acknowledgementId] === true} radio />
                  <span>{ack.text}</span>
                </button>
              ),
            )}
          </section>

          {/* (6) Delivery, contact, fitting — prefilled and SHOWN FOR
              RE-CONFIRMATION, never silently reused. */}
          <section className="pt-section" aria-label={t('review.details.heading')}>
            <h2 className="pt-cutsheet__blockTitle">{t('review.details.heading')}</h2>
            {data.details.onFile ? <Statement>{t('review.details.returning.statement')}</Statement> : null}
            <TextInput label={t('review.details.address.label')} value={address} onChange={setAddress} multiline />
            <TextInput label={t('review.details.contact.label')} value={contact} onChange={setContact} />
            <TextInput label={t('review.details.fitting.label')} value={fitting} onChange={setFitting} />
          </section>

          {/* (7) EL-PRICE with the breakdown and the process statement. */}
          <PriceDisplay
            state="current"
            quote={data.quote}
            tailorName={tailor?.displayName ?? data.tailor.displayName}
            onOpenBreakdown={() => setBreakdownOpen((open) => !open)}
            breakdownOpen={breakdownOpen}
            onGoToOption={() => navigate('/commission/make')}
          />

          {/* ST-REVIEW-REVALIDATED — unchanged is a quiet statement, no
              interruption and no re-confirmation demanded. */}
          {data.revalidated?.changed === false ? (
            <Statement>{t('review.revalidated.unchanged')}</Statement>
          ) : null}

          {/* (8) The facts before the act. D-030(a): the two-fact floor stands
              alone unless the ask action actually ships, in which case the
              corrected third fact renders. */}
          <section className="pt-section">
            <Statement>
              {data.askActionShips
                ? t('review.commitment.full', { tailorName: data.tailor.displayName })
                : t('review.commitment.floor', { tailorName: data.tailor.displayName })}
            </Statement>
          </section>

          {/* Refusals render IN PLACE, above the action, with the server's own
              human copy. The machine code is logged and never shown. */}
          <div ref={refusalRef} tabIndex={-1}>
            {rejection === null ? null : <RefusalOnReview rejection={rejection} data={data} />}
          </div>
          {submission.offline ? <Statement>{t('review.offline.statement')}</Statement> : null}
          {pressed && colourPath === null ? (
            <Statement>{t('review.colourUnchosen.statement')}</Statement>
          ) : null}
          {pressed && !allAcksTaken ? (
            <Statement>{t('error.ACKNOWLEDGEMENT_REQUIRED')}</Statement>
          ) : null}

          {/* (9) THE ONE PROMPT IN THE ENTIRE CLIENT UI. */}
          <div className="pt-actions">
            <Button
              variant="primary"
              busy={submission.busy}
              busyLabel={t('review.submitting.action')}
              onClick={place}
            >
              {t('review.action')}
            </Button>
          </div>
        </>
      )}
    </AppShell>
  );
}

/**
 * Every named refusal state of `SCR-REVIEW`, rendered from the SERVER's code
 * and the server's human copy. `QUOTE_STALE` is rendered AS A DIFF, never as a
 * replacement ([T-54]) — a silently different total at the moment of
 * confirmation is a bait-and-switch even when the arithmetic is honest.
 */
function RefusalOnReview({
  rejection,
  data,
}: {
  rejection: ApiRejection;
  data: ReviewPayload;
}) {
  const body = rejection.body as unknown as {
    code: string;
    message: string;
    detail?: {
      changedSummary?: string;
      fromMinor?: number;
      toMinor?: number;
      totalMinor?: number;
      availableMetres?: number;
      requirementMetres?: number;
    };
  };
  const detail = body.detail ?? {};

  switch (body.code) {
    case 'QUOTE_STALE':
      return (
        <section aria-label={t('review.quotestale.heading')}>
          <h2 className="pt-cutsheet__blockTitle">{t('review.quotestale.heading')}</h2>
          <FactLine>
            {t('review.quotestale.line', {
              changedSummary: detail.changedSummary ?? '',
              fromGBP: money(detail.fromMinor ?? data.quote.totalMinor, data.quote.currency),
              toGBP: money(detail.toMinor ?? data.quote.totalMinor, data.quote.currency),
            })}
          </FactLine>
          <FactLine emphasis>
            {t('review.quotestale.total', {
              priceGBP: money(detail.totalMinor ?? data.quote.totalMinor, data.quote.currency),
            })}
          </FactLine>
          <Statement>{t('review.quotestale.footer')}</Statement>
        </section>
      );
    case 'PRICE_MISMATCH':
      return <ErrorStatement what={t('review.pricemismatch.statement')} assertive />;
    case 'FABRIC_INSUFFICIENT_STOCK':
      return (
        <Statement announce>
          {t('review.stockrace.statement', {
            clothName: data.cloth?.name ?? '',
            commissionTypeLabel: commissionTypeLabel(
              String(data.configuration['commission.type'] ?? ''),
            ),
            requirementMetres: metres(detail.requirementMetres ?? 0),
            availableMetres: metres(detail.availableMetres ?? 0),
          })}
        </Statement>
      );
    case 'MEASUREMENT_INCOMPLETE_FOR_GARMENT':
      return <Statement announce>{refusalCopy(rejection)}</Statement>;
    case 'CONSTRAINT_VIOLATION':
    case 'OPTION_MISSING':
    case 'OPTION_NOT_APPLICABLE':
      return (
        <Statement announce>
          {t('review.constraint.statement', { reason: refusalCopy(rejection) })}
        </Statement>
      );
    case 'ACKNOWLEDGEMENT_STALE':
    case 'ACKNOWLEDGEMENT_REQUIRED':
      // ST-REVIEW-ACKSTALE — the block is simply re-presented above; everything
      // else on the page is preserved.
      return (
        <Statement announce>
          {t('review.ackstale.statement', { clothName: data.cloth?.name ?? '' })}
        </Statement>
      );
    default:
      return <ErrorStatement what={refusalCopy(rejection)} means={t('review.error.means')} assertive />;
  }
}

/* ------------------------------------------------------------ SCR-PLACED */

/**
 * THE LETTER, NOT THE PARTY. Six elements, in this order, and nothing else:
 * the fact · the reference · what happens next · "there is nothing for you to
 * do now" · when he will next hear · a named human.
 *
 * No thanks-formula, no exclamation mark, no celebration, no confetti, no
 * animation, no checkmark flourish. NOTHING IS SOLD HERE.
 */
export function PlacedScreen({ orderRef }: { orderRef: string }) {
  const { navigate } = useNavigation();
  const { tailor } = useHouse();
  const order = useResource(() => api.order(orderRef), [orderRef]);

  return (
    <AppShell title={t('placed.fact')} width="ledger">
      <FactLine emphasis>{t('placed.reference.label')}</FactLine>
      <p className="type-role-record-value">{orderRef}</p>

      <section className="pt-section">
        <h2 className="pt-cutsheet__blockTitle">{t('placed.next.heading')}</h2>
        <ol>
          <li className="pt-row">
            <FactLine>{t('placed.next.1', { tailorName: tailor?.displayName ?? '' })}</FactLine>
          </li>
          <li className="pt-row">
            <FactLine>{t('placed.next.2')}</FactLine>
          </li>
          <li className="pt-row">
            <FactLine>{t('placed.next.3')}</FactLine>
          </li>
        </ol>
      </section>

      <Statement>{t('placed.nothing')}</Statement>
      <Statement>{t('placed.where')}</Statement>

      {/* ST-PLACED-ERROR — the reference is still shown from the placement
          response; the client is never left uncertain whether he ordered. */}
      {order.phase === 'error' ? (
        <Statement>{t('placed.error.statement', { orderRef })}</Statement>
      ) : null}
      {order.phase === 'offline' ? <OfflineStatement /> : null}

      <div className="pt-actions">
        <Button variant="primary" onClick={() => navigate(`/orders/${orderRef}`)}>
          {t('placed.viewOrder')}
        </Button>
      </div>
    </AppShell>
  );
}
